import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2 } from "lucide-react";
import { z } from "zod";
import { cn } from "@/lib/utils";

interface Props { defaultCaseType?: string; compact?: boolean }
type Errors = Partial<Record<"name" | "phone" | "email" | "case_type" | "message", string>>;

export const LeadForm = ({ defaultCaseType, compact }: Props) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    case_type: defaultCaseType || "", message: "",
  });

  const schema = z.object({
    name: z.string().trim().min(2, t("form.errShortName")).max(100),
    phone: z.string().trim().regex(/^[\d\s\-+()]{7,20}$/, t("form.errPhone")),
    email: z.string().trim().email(t("form.errEmail")).max(255).optional().or(z.literal("")),
    case_type: z.string().optional(),
    message: z.string().max(2000).optional(),
  });

  const update = (key: keyof typeof form, value: string) => {
    setForm(f => ({ ...f, [key]: value }));
    if (errors[key as keyof Errors]) setErrors(e => ({ ...e, [key]: undefined }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const newErrors: Errors = {};
      parsed.error.errors.forEach(err => {
        const k = err.path[0] as keyof Errors;
        if (k && !newErrors[k]) newErrors[k] = err.message;
      });
      setErrors(newErrors);
      toast({ title: t("form.errFormTitle"), description: t("form.errFormDesc"), variant: "destructive" });
      return;
    }
    setErrors({});
    setLoading(true);
    const { error } = await supabase.functions.invoke("submit-lead", { body: parsed.data });
    setLoading(false);
    if (error) {
      toast({ title: t("form.errSendTitle"), description: t("form.errSendDesc"), variant: "destructive" });
      return;
    }
    setDone(true);
    toast({ title: t("form.successTitle"), description: t("form.successDesc") });
    setForm({ name: "", phone: "", email: "", case_type: defaultCaseType || "", message: "" });
  };

  if (done) return (
    <div className="text-center py-10 fade-in">
      <CheckCircle2 className="w-16 h-16 text-accent mx-auto mb-4" />
      <h3 className="font-heading text-2xl font-bold mb-2">{t("form.thankTitle")}</h3>
      <p className="text-muted-foreground mb-6">{t("form.thankDesc")}</p>
      <Button variant="outline" onClick={() => setDone(false)}>{t("form.sendAnother")}</Button>
    </div>
  );

  const errClass = (k: keyof Errors) => errors[k] ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className={compact ? "" : "grid sm:grid-cols-2 gap-4"}>
        <div>
          <Label htmlFor="lf-name">{t("form.fullName")} *</Label>
          <Input id="lf-name" required autoComplete="name" value={form.name}
            onChange={e => update("name", e.target.value)}
            aria-invalid={!!errors.name}
            className={cn(errClass("name"))} />
          {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
        </div>
        <div>
          <Label htmlFor="lf-phone">{t("form.phoneLabel")} *</Label>
          <Input id="lf-phone" required type="tel" dir="ltr" autoComplete="tel" value={form.phone}
            onChange={e => update("phone", e.target.value)}
            aria-invalid={!!errors.phone}
            className={cn(errClass("phone"))} />
          {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
        </div>
      </div>
      <div className={compact ? "" : "grid sm:grid-cols-2 gap-4"}>
        <div>
          <Label htmlFor="lf-email">{t("form.emailLabel")}</Label>
          <Input id="lf-email" type="email" dir="ltr" autoComplete="email" value={form.email}
            onChange={e => update("email", e.target.value)}
            aria-invalid={!!errors.email}
            className={cn(errClass("email"))} />
          {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="lf-case">{t("form.caseType")}</Label>
          <Select value={form.case_type} onValueChange={v => update("case_type", v)}>
            <SelectTrigger id="lf-case"><SelectValue placeholder={t("form.casePlaceholder")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={t("practices.personalInjury.caseType")}>{t("form.caseTypes.personalInjury")}</SelectItem>
              <SelectItem value={t("practices.laborLaw.caseType")}>{t("form.caseTypes.laborLaw")}</SelectItem>
              <SelectItem value={t("practices.socialSecurity.caseType")}>{t("form.caseTypes.socialSecurity")}</SelectItem>
              <SelectItem value={t("practices.medicalNegligence.caseType")}>{t("form.caseTypes.medicalNegligence")}</SelectItem>
              <SelectItem value="other">{t("form.caseTypes.other")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="lf-message">{t("form.shortDescription")}</Label>
        <Textarea id="lf-message" rows={4} value={form.message} onChange={e => update("message", e.target.value)} />
      </div>
      <Button type="submit" disabled={loading} className="w-full gradient-gold text-accent-foreground border-0 h-12 text-base font-semibold shadow-gold hover:opacity-90">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("form.send")}
      </Button>
      <p className="text-xs text-muted-foreground text-center">{t("form.disclaimer")}</p>
    </form>
  );
};
